import { alpha, Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateChildComment, useCreateComment } from '@/api/mutations/useCommentMutations';
import { useCommentsDrawer } from '@/comments';
import { BaseButton as Button } from '@/components/BaseButton/BaseButton';
import { useRequiredAppSelector } from '@/hooks';
import { usePrefValue } from '@/preferences';
import { focusField } from '@/utils/helpers';
import { invalidateAfterCommentUpdate } from './helpers';

interface CommentInputWithButtonProps {
    initialText?: string;
    initialValue?: string;
    textAtomContent: string;
    queryArgs: { parentId: number; project_id?: number } | { commentableModel: string; commentableId: number; position: string };
    handleCancel: () => void;
}

export const CommentInputWithButton = (props: CommentInputWithButtonProps) => {
    const { initialText, initialValue = '', textAtomContent, queryArgs, handleCancel } = props;
    const { t } = useTranslation();
    const [drawer, drawerActions] = useCommentsDrawer();
    const episode_id = usePrefValue('editor_episode_id');
    const project_id = useRequiredAppSelector('projectId');
    const ref = useRef<HTMLTextAreaElement>(null);
    const [value, setValue] = useState(initialText ? `> ${initialText}\n\n` : initialValue);
    const { mutateAsync: createChildCommentAsync, status: statusChildCreate } = useCreateChildComment();
    const { mutateAsync: createCommentAsync, status: statusCreate } = useCreateComment();

    useEffect(() => {
        setValue(initialText ? `> ${initialText}\n\n` : '');
    }, [initialText]);

    useEffect(() => {
        if (ref?.current) {
            focusField(ref.current);
        }
    }, []);

    const handleSubmit = async () => {
        if (statusCreate === 'pending' || statusChildCreate === 'pending') {
            return;
        }

        const queryPromise =
            'parentId' in queryArgs
                ? createChildCommentAsync({
                      project_id: queryArgs.project_id ? queryArgs.project_id : project_id,
                      parent_id: queryArgs.parentId,
                      text: value,
                  })
                : createCommentAsync({
                      project_id,
                      commentable_model: queryArgs.commentableModel,
                      commentable_id: queryArgs.commentableId,
                      text: value,
                      position: queryArgs.position,
                      update_text_atom: textAtomContent.replace('{$GROUP}', drawer.group ? drawer.group : ''),
                  });

        const updateRender = () => {
            invalidateAfterCommentUpdate(episode_id)();
            drawerActions.hideTopInput();
            setValue('');
        };

        const { success } = await queryPromise;
        if (success) {
            'parentId' in queryArgs ? setValue('') : updateRender();
        }
    };

    return (
        <div className="flex flex-wrap w-full gap-[12px]">
            <div className="sm:grow-0 sm:basis-[100%] sm:max-w-[100%]">
                <Box
                    sx={theme => ({
                        display: 'inline-flex',
                        height: '32px',
                        margin: '5px 0px 0px',
                        padding: '32px 16px',
                        alignItems: 'center',
                        color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.text.primary,
                        background: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.05) : theme.custom.c7,
                        borderRadius: '16px',
                        width: '100%',
                        '& > textarea:focus': {
                            outline: 'none',
                        },
                        '&:placeholder': {
                            color: theme.palette.info.light,
                        },
                        '& input': {
                            padding: '0',
                        },
                    })}
                >
                    <textarea
                        onKeyPress={e => {
                            if (e.ctrlKey && e.code === 'Enter') {
                                handleSubmit();
                            }
                        }}
                        ref={ref}
                        placeholder={t('comment.enter-text')}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        style={{
                            font: 'inherit',
                            color: 'currentColor',
                            padding: 0,
                            border: 0,
                            background: 'none',
                            display: 'block',
                            width: '100%',
                            animationName: 'mui-auto-fill-cancel',
                            animationDuration: '10ms',
                            resize: 'none',
                            paddingTop: 0,
                        }}
                    />
                </Box>
            </div>

            <div className="flex flex-wrap w-full sm:grow-0 sm:basis-[100%] sm:max-w-[100%] justify-between gap-[20px]">
                <Button variant="destructive" onClick={handleSubmit} className="grow" disabled={!value.length}>
                    {t('buttons.add')}
                </Button>
                <Button variant="outlined" onClick={handleCancel} className="grow">
                    {t('buttons.cancel')}
                </Button>
            </div>
        </div>
    );
};
